import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Doctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    specialization: '',
    consultationFee: '',
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/doctors/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchDoctors();
    } catch (error) {
      console.error('Error deleting doctor:', error);
      alert(error.response?.data?.detail || 'Failed to delete doctor');
    } finally {
      setDeleteLoading(false);
    }
  };

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  }, []);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDoctor) {
        await axios.put(`${API}/doctors/${editingDoctor.id}`, formData);
      } else {
        await axios.post(`${API}/doctors`, formData);
      }
      fetchDoctors();
      resetForm();
    } catch (error) {
      console.error('Error saving doctor:', error);
    }
  };

  const handleEdit = (doctor) => {
    setEditingDoctor(doctor);
    setFormData({
      name: doctor.name,
      specialization: doctor.specialization,
      consultationFee: doctor.consultationFee,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ name: '', specialization: '', consultationFee: '' });
    setEditingDoctor(null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="doctors-title">
            Doctors
          </h1>
          <p className="mt-2" style={{ color: '#6B7280' }}>Manage doctor profiles</p>
        </div>
        <button
          className="primary-btn flex items-center gap-2"
          onClick={() => setShowForm(true)}
          data-testid="add-doctor-btn"
        >
          <Plus size={20} />
          Add Doctor
        </button>
      </div>

      {showForm && (
        <div className="card p-6 mb-8" data-testid="doctor-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            {editingDoctor ? 'Edit Doctor' : 'Add New Doctor'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="doctor-name-input"
                />
              </div>
              <div>
                <label>Specialization</label>
                <input
                  type="text"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  required
                  data-testid="doctor-specialization-input"
                />
              </div>
              <div>
                <label>Consultation Fee (₹)</label>
                <input
                  type="number"
                  value={formData.consultationFee}
                  onChange={(e) => setFormData({ ...formData, consultationFee: e.target.value })}
                  required
                  data-testid="doctor-fee-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="primary-btn" data-testid="doctor-submit-btn">
                {editingDoctor ? 'Update' : 'Save'}
              </button>
              <button type="button" className="secondary-btn" onClick={resetForm} data-testid="doctor-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {doctors.map((doctor) => (
          <div key={doctor.id} className="card p-6" data-testid={`doctor-card-${doctor.id}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
                  {doctor.name}
                </h3>
                <p className="text-sm mt-1" style={{ color: '#6B7280' }}>{doctor.specialization}</p>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleEdit(doctor)}
                  className="p-2 rounded hover:bg-gray-100 transition-colors"
                  data-testid={`edit-doctor-${doctor.id}`}
                >
                  <Edit size={18} style={{ color: '#990099' }} />
                </button>
                <button
                  onClick={() => setDeleteTarget(doctor)}
                  className="p-2 rounded hover:bg-gray-100 transition-colors"
                  data-testid={`delete-doctor-${doctor.id}`}
                >
                  <Trash2 size={18} style={{ color: '#EF4444' }} />
                </button>
              </div>
            </div>
            <div className="pt-4 border-t" style={{ borderColor: '#E5E0D8' }}>
              <p className="text-sm" style={{ color: '#78716C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Consultation Fee
              </p>
              <p className="text-2xl font-bold mt-1" style={{ color: '#990099' }}>
                ₹{doctor.consultationFee}
              </p>
            </div>
          </div>
        ))}
      </div>

      {doctors.length === 0 && !showForm && (
        <div className="card p-12 text-center" data-testid="no-doctors">
          <p style={{ color: '#6B7280' }}>No doctors added yet. Click "Add Doctor" to get started.</p>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Doctor"
        message={`Are you sure you want to delete ${deleteTarget?.name}? This action cannot be undone.`}
        confirmLabel="Delete Doctor"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        testIdPrefix="delete-doctor"
      />
    </div>
  );
};

export default Doctors;
